import { useState } from 'react';
import { Lock, Unlock, X, Wallet, ShieldAlert, PenLine, CheckCircle2 } from 'lucide-react';

export type HoldMode = 'hold' | 'release' | 'note' | 'clear';
type Reason = 'unpaid_fees' | 'disciplinary' | 'manual';

const REASONS: { k: Reason; label: string; hint: string; icon: any }[] = [
  { k: 'unpaid_fees', label: 'Unpaid fees', hint: 'Opens by itself once the fees are paid.', icon: Wallet },
  { k: 'disciplinary', label: 'Disciplinary', hint: 'Stays locked until you release it.', icon: ShieldAlert },
  { k: 'manual', label: 'Other reason', hint: 'Stays locked until you release it.', icon: PenLine },
];
const CHIPS: Record<Reason, string[]> = {
  unpaid_fees: ['Please clear the outstanding school fees to see this result.', 'Balance for books and uniform is still owed.', 'Please complete the second instalment.'],
  disciplinary: ['Please see the principal with your child.', 'Result withheld pending a disciplinary review.'],
  manual: ['Please visit the school office.', 'Some school property has not been returned.'],
};

/**
 * One designed box for every hold action on a result:
 *  - hold: lock a result (even one already published) with a reason and a note the parent sees;
 *  - note: change the note on a held result;
 *  - release: unlock it, with a note kept in the audit log;
 *  - clear: record that the fees were paid (cash/bank at the bursary).
 */
export function HoldNoteModal({ mode, student, currentNote, currentReason, busy, onClose, onSubmit }: {
  mode: HoldMode; student: string; currentNote?: string | null; currentReason?: string | null; busy?: boolean;
  onClose: () => void; onSubmit: (v: { reason: Reason; note: string }) => void;
}) {
  const [reason, setReason] = useState<Reason>((currentReason as Reason) || 'unpaid_fees');
  const [note, setNote] = useState(mode === 'note' ? currentNote ?? '' : '');
  const meta = {
    hold: { title: `Hold ${student}'s result`, icon: Lock, tone: 'bg-amber-100 text-amber-700', label: 'Note for the parent and student', ph: 'What they need to do to see the result', button: 'Hold result', btn: 'bg-amber-600 hover:bg-amber-700' },
    note: { title: 'Change the hold note', icon: PenLine, tone: 'bg-amber-100 text-amber-700', label: 'Note for the parent and student', ph: 'What they need to do to see the result', button: 'Save note', btn: 'bg-[#0A2540] hover:bg-slate-800' },
    release: { title: `Release ${student}'s result`, icon: Unlock, tone: 'bg-green-100 text-green-700', label: 'Why are you releasing it? (kept in the audit log)', ph: 'e.g. Balance paid at the bursary, receipt 0042', button: 'Release result', btn: 'bg-green-600 hover:bg-green-700' },
    clear: { title: `Mark ${student}'s fees as paid`, icon: CheckCircle2, tone: 'bg-blue-100 text-blue-700', label: 'Payment note', ph: 'e.g. Paid cash ₦20,000, receipt 0042', button: 'Mark as paid', btn: 'bg-[#0A2540] hover:bg-slate-800' },
  }[mode];
  const Icon = meta.icon;
  const ok = note.trim().length >= 3;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="px-5 pt-5 pb-3 flex items-start gap-3 border-b border-slate-100">
          <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${meta.tone}`}><Icon className="w-5 h-5" /></span>
          <div className="flex-1 min-w-0"><h3 className="font-bold text-slate-900 text-lg leading-tight">{meta.title}</h3>
            {mode === 'hold' && <p className="text-xs text-slate-500 mt-0.5">The parent and student will see the note instead of the result. You can release it at any time.</p>}
            {mode === 'release' && <p className="text-xs text-slate-500 mt-0.5">The parent and student will see the result straight away. You can hold it again later.</p>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          {(mode === 'release' || mode === 'clear') && currentNote && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-sm"><p className="text-[11px] font-bold uppercase text-amber-700">Current hold note</p><p className="text-amber-900">{currentNote}</p></div>
          )}

          {mode === 'hold' && (
            <div className="grid sm:grid-cols-3 gap-2">
              {REASONS.map((r) => (
                <button key={r.k} type="button" onClick={() => setReason(r.k)}
                  className={`text-left rounded-xl border p-3 ${reason === r.k ? 'border-[#0A2540] ring-2 ring-[#0A2540]/10 bg-slate-50' : 'border-slate-200'}`}>
                  <r.icon className={`w-4 h-4 ${reason === r.k ? 'text-[#0A2540]' : 'text-slate-400'}`} />
                  <p className="text-sm font-semibold text-slate-900 mt-1">{r.label}</p>
                  <p className="text-[11px] text-slate-500 leading-snug">{r.hint}</p>
                </button>
              ))}
            </div>
          )}

          <label className="block">
            <span className="text-sm font-semibold text-slate-700">{meta.label}</span>
            <textarea autoFocus rows={3} maxLength={255} value={note} onChange={(e) => setNote(e.target.value)} placeholder={meta.ph}
              className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm focus:border-[#0A2540] focus:ring-2 focus:ring-[#0A2540]/10 outline-none" />
            <span className="text-[11px] text-slate-400">{note.length}/255</span>
          </label>

          {(mode === 'hold' || mode === 'note') && (
            <div className="flex flex-wrap gap-1.5">
              {CHIPS[mode === 'note' ? ((currentReason as Reason) || 'manual') : reason].map((c) => (
                <button key={c} type="button" onClick={() => setNote(c)} className="text-xs px-2.5 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700">{c}</button>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-slate-300 text-sm font-bold text-slate-700">Cancel</button>
          <button disabled={!ok || busy} onClick={() => onSubmit({ reason, note: note.trim() })} className={`flex-1 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-40 ${meta.btn}`}>{busy ? 'Saving…' : meta.button}</button>
        </div>
      </div>
    </div>
  );
}
